import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DollarSign, TrendingUp, Activity, RefreshCw, AlertTriangle,
  Trophy, BarChart3, Sparkles, ShieldAlert, Zap, Gift, HardDrive, Loader2,
  Coins, Clock, Trash2,
} from 'lucide-react';
import PlaidStatusBadge from './PlaidStatusBadge';
import OpenRouterBalanceCard from './OpenRouterBalanceCard';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/owner-admin/cost-dashboard`;

const FETCH_OPTS = { credentials: 'include' };

function fmtUsd(n) {
  if (n == null) return '$0.00';
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Number(n).toFixed(2)}`;
}

function KpiCard({ title, value, subtitle, icon: Icon, tone = 'emerald', testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-xl bg-${tone}-500/10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 text-${tone}-500`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Sparkline({ data, width = 480, height = 60, color = '#10b981' }) {
  if (!data || data.length < 2) {
    return <div className="text-xs text-muted-foreground py-6 text-center">No data yet</div>;
  }
  const max = Math.max(...data.map(d => d.spend), 0.0001);
  const stepX = width / (data.length - 1);
  const pts = data.map((d, i) => `${i * stepX},${height - (d.spend / max) * (height - 4) - 2}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="block">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

export default function OwnerAdminCostDashboard() {
  const [kpis, setKpis] = useState(null);
  const [topSpenders, setTopSpenders] = useState([]);
  const [features, setFeatures] = useState([]);
  const [trend, setTrend] = useState([]);
  const [margins, setMargins] = useState([]);
  const [roi, setRoi] = useState(null);
  const [roiSort, setRoiSort] = useState('worst');
  const [referrals, setReferrals] = useState([]);
  const [storage, setStorage] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [overageRev, setOverageRev] = useState(null);
  const [retention, setRetention] = useState(null);
  const [purging, setPurging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [k, t, f, tr, m, roiRes, ref, st, ov, ret] = await Promise.all([
        fetch(BASE, FETCH_OPTS),
        fetch(`${BASE}/top?limit=15`, FETCH_OPTS),
        fetch(`${BASE}/features`, FETCH_OPTS),
        fetch(`${BASE}/trend?days=30`, FETCH_OPTS),
        fetch(`${BASE}/margins?threshold_pct=75`, FETCH_OPTS),
        fetch(`${BASE}/customer-roi`, FETCH_OPTS),
        fetch(`${BASE}/referrals?limit=15`, FETCH_OPTS),
        fetch(`${BASE}/storage-status`, FETCH_OPTS),
        fetch(`${BASE}/overage-revenue`, FETCH_OPTS),
        fetch(`${BASE}/retention-status`, FETCH_OPTS),
      ]);
      if (!k.ok) throw new Error(`KPIs ${k.status}`);
      setKpis(await k.json());
      setTopSpenders(t.ok ? await t.json() : []);
      setFeatures(f.ok ? await f.json() : []);
      setTrend(tr.ok ? await tr.json() : []);
      setMargins(m.ok ? await m.json() : []);
      setRoi(roiRes.ok ? await roiRes.json() : null);
      setReferrals(ref.ok ? await ref.json() : []);
      setStorage(st.ok ? await st.json() : null);
      setOverageRev(ov.ok ? await ov.json() : null);
      setRetention(ret.ok ? await ret.json() : null);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const runMigration = async () => {
    setMigrating(true);
    try {
      const r = await fetch(`${BASE}/storage-status/migrate-now?batch=50`, { credentials: 'include', ...FETCH_OPTS, method: 'POST' });
      if (r.ok) {
        const result = await r.json();
        alert(`Migration pass: ${result.migrated} migrated, ${result.errors || 0} errors`);
        await refresh();
      } else {
        alert(`Migration failed: ${r.status}`);
      }
    } catch (e) {
      alert(`Migration error: ${e.message}`);
    }
    setMigrating(false);
  };

  const runRetentionPurge = async () => {
    if (!confirm('Hard-delete documents older than the retention horizon? This cannot be undone.')) return;
    setPurging(true);
    try {
      const r = await fetch(`${BASE}/retention-status/purge-now?batch=100`, { credentials: 'include', ...FETCH_OPTS, method: 'POST' });
      if (r.ok) {
        const result = await r.json();
        alert(`Purge complete: ${result.deleted} deleted, ${result.skipped_evergreen} evergreen skipped, ${result.r2_errors} R2 errors`);
        await refresh();
      } else {
        alert(`Purge failed: ${r.status}`);
      }
    } catch (e) {
      alert(`Purge error: ${e.message}`);
    }
    setPurging(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-cost-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">AI Cost & Margin Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Live view of OpenRouter spend, workspace top-spenders, and margin health from the usage ledger.</p>
        </div>
        <Button onClick={refresh} variant="outline" size="sm" data-testid="cost-dash-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="p-4 flex items-center gap-2 text-red-500 text-sm">
            <AlertTriangle className="w-4 h-4" /> {error}
          </CardContent>
        </Card>
      )}

      {/* Plaid integration health */}
      <PlaidStatusBadge />

      {/* AI supplier credit balance — admin-only, never shown to end users. */}
      <OpenRouterBalanceCard />

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          title="Revenue (marked-up)"
          value={fmtUsd(kpis?.total_marked_usd || 0)}
          subtitle={`${kpis?.workspaces_with_activity || 0} active workspaces`}
          icon={DollarSign}
          tone="emerald"
          testId="kpi-revenue"
        />
        <KpiCard
          title="OpenRouter Raw Cost"
          value={fmtUsd(kpis?.total_raw_usd || 0)}
          subtitle="What we actually pay"
          icon={Zap}
          tone="amber"
          testId="kpi-raw-cost"
        />
        <KpiCard
          title="Blended Margin"
          value={`${kpis?.blended_margin_pct || 0}%`}
          subtitle="Healthy at 70%+"
          icon={TrendingUp}
          tone="violet"
          testId="kpi-margin"
        />
        <KpiCard
          title="Total LLM Calls"
          value={(kpis?.total_calls || 0).toLocaleString()}
          subtitle="Month-to-date"
          icon={Activity}
          tone="blue"
          testId="kpi-calls"
        />
        <KpiCard
          title="Overage Revenue"
          value={fmtUsd(overageRev?.lifetime_usd || 0)}
          subtitle={`${overageRev?.active_users || 0} opted in · ${fmtUsd(overageRev?.last_30_days_usd || 0)} last 30d`}
          icon={Coins}
          tone="emerald"
          testId="kpi-overage-revenue"
        />
      </div>

      {/* Trend + Feature breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="trend-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5" /> 30-Day AI Spend Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Sparkline data={trend} />
            <div className="flex justify-between text-[11px] text-muted-foreground mt-2">
              <span>{trend[0]?.date || '—'}</span>
              <span className="font-medium text-foreground">
                {trend.length > 0 && `Today: ${fmtUsd(trend[trend.length - 1]?.spend)}`}
              </span>
              <span>{trend[trend.length - 1]?.date || '—'}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="features-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-5 h-5" /> Spend by Feature
            </CardTitle>
          </CardHeader>
          <CardContent>
            {features.length === 0 ? (
              <p className="text-sm text-muted-foreground">No LLM activity yet this month.</p>
            ) : (
              <div className="space-y-2">
                {features.map((f) => (
                  <div key={f.feature} className="flex justify-between text-sm">
                    <span className="capitalize">{f.feature.replace(/_/g, ' ')}</span>
                    <span className="font-medium" data-testid={`feature-${f.feature}`}>
                      {fmtUsd(f.spend)} <span className="text-muted-foreground text-xs">· {f.calls}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Spenders */}
      <Card data-testid="top-spenders-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5" /> Top-Spender Workspaces
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topSpenders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No workspaces with LLM activity this month.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Workspace</th>
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-4 font-medium">Calls</th>
                    <th className="py-2 pr-4 font-medium">Raw cost</th>
                    <th className="py-2 pr-4 font-medium">Marked-up</th>
                  </tr>
                </thead>
                <tbody>
                  {topSpenders.map((s) => (
                    <tr key={s.workspace_id} className="border-b hover:bg-muted/40" data-testid={`spender-row-${s.workspace_id}`}>
                      <td className="py-2 pr-4 font-medium truncate max-w-[260px]">{s.workspace_name}</td>
                      <td className="py-2 pr-4 capitalize">
                        <Badge variant="outline" className="text-[10px]">{s.plan_id}</Badge>
                      </td>
                      <td className="py-2 pr-4">{s.calls}</td>
                      <td className="py-2 pr-4 text-amber-600">{fmtUsd(s.raw_cost_usd)}</td>
                      <td className="py-2 pr-4 font-semibold text-emerald-600">{fmtUsd(s.marked_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Margin watchlist */}
      <Card className="border-red-500/30" data-testid="margin-watchlist-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldAlert className="w-5 h-5 text-red-500" /> Margin Watchlist
            <Badge variant="outline" className="ml-2 text-[10px]">&lt; 75% margin</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {margins.length === 0 ? (
            <p className="text-sm text-muted-foreground">All workspaces are within healthy margins. 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Workspace</th>
                    <th className="py-2 pr-4 font-medium">Plan</th>
                    <th className="py-2 pr-4 font-medium">Plan Rev</th>
                    <th className="py-2 pr-4 font-medium">Raw Cost</th>
                    <th className="py-2 pr-4 font-medium">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {margins.map((m) => (
                    <tr key={m.workspace_id} className="border-b hover:bg-muted/40" data-testid={`margin-row-${m.workspace_id}`}>
                      <td className="py-2 pr-4 font-medium truncate max-w-[260px]">{m.workspace_name}</td>
                      <td className="py-2 pr-4 capitalize">
                        <Badge variant="outline" className="text-[10px]">{m.plan_id}</Badge>
                      </td>
                      <td className="py-2 pr-4">{fmtUsd(m.revenue_usd)}</td>
                      <td className="py-2 pr-4 text-amber-600">{fmtUsd(m.raw_cost_usd)}</td>
                      <td className="py-2 pr-4">
                        <Badge className={m.margin_pct < 0 ? 'bg-red-500/15 text-red-600' : m.margin_pct < 50 ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}>
                          {m.margin_pct}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer ROI — best & worst clients */}
      <Card className="border-emerald-500/30" data-testid="customer-roi-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-emerald-500" /> Customer ROI
            <Badge variant="outline" className="ml-1 text-[10px]">MRR vs AI cost · this month</Badge>
            <span className="ml-auto flex gap-1">
              <Button size="sm" variant={roiSort === 'worst' ? 'default' : 'outline'} data-testid="roi-sort-worst"
                onClick={() => setRoiSort('worst')}>Worst first</Button>
              <Button size="sm" variant={roiSort === 'best' ? 'default' : 'outline'} data-testid="roi-sort-best"
                onClick={() => setRoiSort('best')}>Best first</Button>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!roi?.rows?.length ? (
            <p className="text-sm text-muted-foreground">No customer billing or usage recorded this month yet.</p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap gap-4 text-xs text-muted-foreground" data-testid="roi-totals">
                <span>{roi.totals.customers} customers</span>
                <span>MRR: <strong className="text-emerald-600">{fmtUsd(roi.totals.mrr_usd)}</strong></span>
                <span>AI cost: <strong className="text-amber-600">{fmtUsd(roi.totals.ai_cost_usd)}</strong></span>
                <span>Net: <strong className={roi.totals.profit_usd >= 0 ? 'text-emerald-600' : 'text-red-600'}>{fmtUsd(roi.totals.profit_usd)}</strong></span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Customer</th>
                      <th className="py-2 pr-4 font-medium">Plan</th>
                      <th className="py-2 pr-4 font-medium">Pays / mo</th>
                      <th className="py-2 pr-4 font-medium">AI Cost</th>
                      <th className="py-2 pr-4 font-medium">Calls</th>
                      <th className="py-2 pr-4 font-medium">Net / mo</th>
                      <th className="py-2 pr-4 font-medium">Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...roi.rows]
                      .sort((a, b) => (roiSort === 'best' ? b.profit_usd - a.profit_usd : a.profit_usd - b.profit_usd))
                      .slice(0, 25)
                      .map((r) => (
                        <tr key={r.user_id} className="border-b hover:bg-muted/40" data-testid={`roi-row-${r.user_id}`}>
                          <td className="py-2 pr-4 max-w-[220px]">
                            <p className="truncate font-medium">{r.name !== '—' ? r.name : r.email}</p>
                            <p className="truncate text-xs text-muted-foreground">{r.email}</p>
                          </td>
                          <td className="py-2 pr-4"><Badge variant="outline" className="text-[10px] capitalize">{r.plan_id}</Badge></td>
                          <td className="py-2 pr-4">{fmtUsd(r.mrr_usd)}</td>
                          <td className="py-2 pr-4 text-amber-600">{fmtUsd(r.ai_cost_usd)}</td>
                          <td className="py-2 pr-4">{r.calls}</td>
                          <td className={`py-2 pr-4 font-semibold ${r.profit_usd >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtUsd(r.profit_usd)}</td>
                          <td className="py-2 pr-4">
                            {r.margin_pct === null ? (
                              <span className="text-xs text-muted-foreground">free</span>
                            ) : (
                              <Badge className={r.margin_pct < 0 ? 'bg-red-500/15 text-red-600' : r.margin_pct < 50 ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}>
                                {r.margin_pct}%
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      {/* Storage Migration */}
      {storage && (
        <Card data-testid="storage-migration-card" className="border-blue-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <HardDrive className="w-5 h-5 text-blue-500" /> Document Storage Migration
              <Badge variant="outline" className="ml-2 text-[10px]">
                {storage.r2_configured ? `R2 → ${storage.r2_bucket}` : 'Not configured'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total docs</p>
                <p className="text-2xl font-bold mt-1">{(storage.total_docs || 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">On Cloudflare R2</p>
                <p className="text-2xl font-bold mt-1 text-emerald-500" data-testid="storage-on-r2">{(storage.on_r2 || 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Still in Mongo</p>
                <p className="text-2xl font-bold mt-1 text-amber-500" data-testid="storage-legacy">{(storage.on_mongo_b64 || 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">% Migrated</p>
                <p className="text-2xl font-bold mt-1 text-violet-500">{storage.pct_migrated}%</p>
              </div>
            </div>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-violet-500" style={{ width: `${storage.pct_migrated}%` }} />
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-muted-foreground">
                Background migration runs every 60s in batches of 25. You can also kick a manual pass below (drains up to 50 docs).
              </p>
              <Button
                size="sm" variant="outline"
                onClick={runMigration}
                disabled={!storage.r2_configured || migrating || storage.on_mongo_b64 === 0}
                data-testid="storage-migrate-btn"
              >
                {migrating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                {storage.on_mongo_b64 === 0 ? 'All migrated' : 'Run pass now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Retention */}
      {retention && (
        <Card data-testid="retention-card" className="border-amber-500/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-amber-500" /> Document Retention
              <Badge variant="outline" className="ml-2 text-[10px]">
                {retention.retention_days}-day horizon
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total docs</p>
                <p className="text-2xl font-bold mt-1">{(retention.total_docs || 0).toLocaleString()}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Eligible for purge</p>
                <p className="text-2xl font-bold mt-1 text-amber-500" data-testid="retention-eligible">
                  {(retention.expired_eligible || 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Lifetime purged</p>
                <p className="text-2xl font-bold mt-1 text-violet-500" data-testid="retention-purged">
                  {(retention.lifetime_purged || 0).toLocaleString()}
                </p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Last purge</p>
                <p className="text-sm font-medium mt-2">
                  {retention.last_purge_at ? new Date(retention.last_purge_at).toLocaleDateString() : 'Never'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Auto-runs every 24h. "Approved" / "filed" docs linked to invoices/bills/transactions are evergreen and never purged.
              </p>
              <Button
                size="sm" variant="outline"
                onClick={runRetentionPurge}
                disabled={purging || retention.expired_eligible === 0}
                data-testid="retention-purge-btn"
                className="border-amber-500/40"
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                {retention.expired_eligible === 0 ? 'Nothing to purge' : 'Purge now'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral Leaderboard */}
      <Card data-testid="referral-leaderboard-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gift className="w-5 h-5 text-emerald-500" /> Referral Leaderboard
            <Badge variant="outline" className="ml-2 text-[10px]">All-time</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No referrals redeemed yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Champion</th>
                    <th className="py-2 pr-4 font-medium">Code</th>
                    <th className="py-2 pr-4 font-medium">Redemptions</th>
                    <th className="py-2 pr-4 font-medium">Unused balance</th>
                    <th className="py-2 pr-4 font-medium">Last redeem</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r.user_id} className="border-b hover:bg-muted/40" data-testid={`referral-row-${r.user_id}`}>
                      <td className="py-2 pr-4">
                        <div className="font-medium truncate max-w-[220px]">{r.name || r.email}</div>
                        <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">{r.email}</div>
                      </td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="font-mono text-[11px]">{r.referral_code || '—'}</Badge>
                      </td>
                      <td className="py-2 pr-4">
                        <span className="text-lg font-bold text-emerald-500">{r.redemptions}</span>
                      </td>
                      <td className="py-2 pr-4">{fmtUsd(r.user_balance_usd)}</td>
                      <td className="py-2 pr-4 text-[12px] text-muted-foreground">
                        {r.last_redemption_at ? new Date(r.last_redemption_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[11px] text-muted-foreground mt-3">
                Flag rings: same IP/device, repeated 1-redemption fresh accounts, or sudden spikes ≥ 10 redemptions/day.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
